import { useState } from 'react';
import { X, Save, Loader2, Flame, CheckCircle2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import type { Job } from '../lib/store';
import { useAuth } from '../lib/auth';

const BOILER_TYPES = ['Combi', 'System', 'Regular / Heat-only', 'Back Boiler'];
const GAS_TYPES = ['Natural Gas', 'LPG', 'Oil'];

const SAFETY_CHECKS = [
  'Flue system inspected and clear',
  'Case seals intact',
  'Gas rate / operating pressure correct',
  'Ventilation adequate',
  'Condensate trap clear',
  'Expansion vessel pressure OK',
  'Water pressure correct (1–1.5 bar cold)',
  'Ignition and burner operation OK',
  'Controls / thermostat functioning',
  'CO alarm present / recommended',
];

// Small helper - RFC4122-ish id (crypto.randomUUID may not be everywhere)
function genId(): string {
  try {
    // @ts-ignore
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through
  }
  return 'r_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function BoilerServiceReportModal({
  job,
  editingReport,
  onClose,
  onSaved,
}: {
  job: Job;
  editingReport?: any | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();

  // Prefer the explicit editing report; fall back to legacy single service_report only when creating first cert.
  const existing: any = editingReport || {};
  const [appliance_make, setMake] = useState<string>(existing.appliance_make || '');
  const [appliance_model, setModel] = useState<string>(existing.appliance_model || '');
  const [serial_number, setSerial] = useState<string>(existing.serial_number || '');
  const [boiler_type, setBoilerType] = useState<string>(existing.boiler_type || 'Combi');
  const [gas_type, setGasType] = useState<string>(existing.gas_type || 'Natural Gas');
  const [location, setLocation] = useState<string>(existing.location || 'Kitchen');

  // Combustion analysis
  const [co_ppm, setCO] = useState<string>(existing.co_ppm || '');
  const [co2_pct, setCO2] = useState<string>(existing.co2_pct || '');
  const [co_co2_ratio, setRatio] = useState<string>(existing.co_co2_ratio || '');
  const [operating_pressure, setPressure] = useState<string>(existing.operating_pressure || '');

  const [checks, setChecks] = useState<Record<string, boolean>>(
    existing.checks || Object.fromEntries(SAFETY_CHECKS.map((c) => [c, true]))
  );

  const [faults, setFaults] = useState<string>(existing.faults || '');
  const [recommendations, setRecs] = useState<string>(existing.recommendations || '');
  const [safe_to_use, setSafeToUse] = useState<boolean>(
    existing.safe_to_use !== undefined ? existing.safe_to_use : true
  );

  const [saving, setSaving] = useState(false);

  const toggleCheck = (c: string) => setChecks((s) => ({ ...s, [c]: !s[c] }));

  const save = async () => {
    if (!appliance_make.trim() || !appliance_model.trim()) {
      return toast.error('Please enter boiler make and model');
    }
    setSaving(true);
    try {
      // Build the report object
      const report: any = {
        id: existing.id || genId(),
        kind: 'boiler_service',
        appliance_make: appliance_make.trim(),
        appliance_model: appliance_model.trim(),
        serial_number: serial_number.trim() || null,
        boiler_type,
        gas_type,
        location: location.trim() || null,
        co_ppm: co_ppm || null,
        co2_pct: co2_pct || null,
        co_co2_ratio: co_co2_ratio || null,
        operating_pressure: operating_pressure || null,
        checks,
        faults: faults.trim() || null,
        recommendations: recommendations.trim() || null,
        safe_to_use,
        engineer: user?.full_name || user?.username || '',
        // preserve original created time if editing, otherwise stamp new
        completed_at: existing.completed_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Get current list (backend now returns service_reports; fall back to legacy service_report)
      const j: any = job as any;
      const currentList: any[] = Array.isArray(j.service_reports)
        ? [...j.service_reports]
        : j.service_report && j.service_report.kind
        ? [j.service_report]
        : [];

      const idx = existing.id ? currentList.findIndex((r) => r && r.id === existing.id) : -1;
      if (idx >= 0) {
        currentList[idx] = report;
      } else {
        currentList.push(report);
      }

      const payload = { service_reports: currentList };
      await api.put(`/jobs/${job.id}`, payload);
      toast.success(existing.id ? 'Boiler service report updated' : 'Boiler service report saved');
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-pop overflow-hidden max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 grid place-items-center">
              <Flame className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">
                {existing.id ? 'Edit Boiler Service Report' : 'Boiler Service Report'}
              </h2>
              <p className="text-xs text-slate-500">
                {job.job_number} · {format(new Date(), 'd MMM yyyy')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Appliance */}
          <Section title="Appliance">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Make *">
                <input className="input" placeholder="e.g. Worcester Bosch" value={appliance_make} onChange={(e) => setMake(e.target.value)} />
              </Field>
              <Field label="Model *">
                <input className="input" placeholder="e.g. Greenstar 30i" value={appliance_model} onChange={(e) => setModel(e.target.value)} />
              </Field>
              <Field label="Serial number">
                <input className="input" value={serial_number} onChange={(e) => setSerial(e.target.value)} />
              </Field>
              <Field label="Location">
                <input className="input" placeholder="e.g. Kitchen cupboard" value={location} onChange={(e) => setLocation(e.target.value)} />
              </Field>
              <Field label="Type">
                <select className="input" value={boiler_type} onChange={(e) => setBoilerType(e.target.value)}>
                  {BOILER_TYPES.map((t) => (<option key={t}>{t}</option>))}
                </select>
              </Field>
              <Field label="Fuel">
                <select className="input" value={gas_type} onChange={(e) => setGasType(e.target.value)}>
                  {GAS_TYPES.map((t) => (<option key={t}>{t}</option>))}
                </select>
              </Field>
            </div>
          </Section>

          {/* Combustion */}
          <Section title="Combustion analysis (optional)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="CO (ppm)">
                <input type="number" className="input" placeholder="e.g. 12" value={co_ppm} onChange={(e) => setCO(e.target.value)} />
              </Field>
              <Field label="CO₂ (%)">
                <input type="number" step="0.01" className="input" placeholder="e.g. 8.5" value={co2_pct} onChange={(e) => setCO2(e.target.value)} />
              </Field>
              <Field label="CO/CO₂ ratio">
                <input type="number" step="0.0001" className="input" placeholder="e.g. 0.0015" value={co_co2_ratio} onChange={(e) => setRatio(e.target.value)} />
              </Field>
              <Field label="Op. pressure (bar)">
                <input type="number" step="0.1" className="input" placeholder="e.g. 1.3" value={operating_pressure} onChange={(e) => setPressure(e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Safety checks */}
          <Section title="Safety & operation checks">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SAFETY_CHECKS.map((c) => (
                <label key={c} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-slate-50">
                  <input type="checkbox" checked={!!checks[c]} onChange={() => toggleCheck(c)} className="mt-0.5" />
                  <span className={checks[c] ? 'text-slate-900' : 'text-slate-500'}>{c}</span>
                </label>
              ))}
            </div>
          </Section>

          {/* Faults / recs */}
          <Section title="Faults & recommendations">
            <Field label="Faults found">
              <textarea className="input min-h-[70px]" placeholder="Any faults observed during the service" value={faults} onChange={(e) => setFaults(e.target.value)} />
            </Field>
            <div className="mt-3">
              <Field label="Recommendations">
                <textarea className="input min-h-[70px]" placeholder="Advice for the customer" value={recommendations} onChange={(e) => setRecs(e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Safety verdict + engineer */}
          <Section title="Sign-off">
            <div className="space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={safe_to_use} onChange={() => setSafeToUse(true)} className="mt-1" />
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-semibold text-slate-900">Appliance safe for continued use</span>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={!safe_to_use} onChange={() => setSafeToUse(false)} className="mt-1" />
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="font-semibold text-slate-900">Appliance NOT safe — action required</span>
                </div>
              </label>
              <div className="pt-2">
                <Field label="Engineer">
                  <input className="input bg-slate-50" value={user?.full_name || user?.username || ''} readOnly />
                </Field>
              </div>
            </div>
          </Section>
        </div>

        <div className="border-t border-slate-100 px-5 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" disabled={saving}>Skip for now</button>
          <button onClick={save} disabled={saving} className="btn btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save report
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
